import { useEffect, useState } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const WA_NUMBER = '6287770970557';
const IMG_BASE = (process.env.REACT_APP_IMG_URL || 'http://localhost:3000/uploads') + '/';

interface Product {
  id: number;
  name: string;
  sku: string;
  description: string;
  image: string | null;
  stock: number;
  selling_price: number;
  location_id: number;
  location_name: string;
  is_po: number;
}

interface CartItem extends Product { qty: number; }

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const CART_KEY = 'temanskrispy_cart';
const loadCart = (): CartItem[] => { try { return JSON.parse(localStorage.getItem(CART_KEY) || '[]'); } catch { return []; } };
const saveCart = (cart: CartItem[]) => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); };

export default function Katalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'ready' | 'po'>('all');

  // Regular cart
  const [cart, setCart] = useState<CartItem[]>(loadCart);
  const [showCart, setShowCart] = useState(false);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ customer_name: '', customer_phone: '', note: '' });
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');

  // PO cart
  const [poCart, setPOCart] = useState<CartItem[]>([]);
  const [showPOCart, setShowPOCart] = useState(false);
  const [poAddedId, setPOAddedId] = useState<number | null>(null);

  // PO checkout
  const [showPOCheckout, setShowPOCheckout] = useState(false);
  const [poCheckoutForm, setPOCheckoutForm] = useState({ customer_name: '', customer_phone: '', note: '' });
  const [poSubmitting, setPOSubmitting] = useState(false);
  const [poError, setPOError] = useState('');
  const [poSuccess, setPOSuccess] = useState('');

  useEffect(() => {
    axios.get(`${BASE_URL}/catalog/products`)
      .then((res) => setProducts(res.data))
      .catch(() => setError('Gagal memuat katalog produk'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { saveCart(cart); }, [cart]);

  const filtered = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'all' || (filterType === 'po' ? !!p.is_po : !p.is_po);
    return matchSearch && matchType;
  });

  const hasPO = products.some((p) => !!p.is_po);

  // ── Regular cart ──────────────────────────────────────
  const addToCart = (product: Product) => {
    setCart((prev) => {
      const exist = prev.find((i) => i.id === product.id);
      if (exist) {
        if (exist.qty >= product.stock) return prev;
        return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...product, qty: 1 }];
    });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 1200);
  };
  const updateQty = (id: number, qty: number) => {
    if (qty <= 0) setCart((prev) => prev.filter((i) => i.id !== id));
    else setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };
  const clearCart = () => setCart([]);
  const totalItems = cart.reduce((acc, i) => acc + i.qty, 0);
  const totalPrice = cart.reduce((acc, i) => acc + i.qty * i.selling_price, 0);

  const openCheckout = () => {
    if (cart.length === 0) return;
    setCheckoutForm({ customer_name: '', customer_phone: '', note: '' });
    setCheckoutError('');
    setCheckoutSuccess('');
    setShowCart(false);
    setShowCheckout(true);
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setCheckoutSubmitting(true);
    setCheckoutError('');
    try {
      const res = await axios.post(`${BASE_URL}/catalog-orders/createBulk`, {
        customer_name: checkoutForm.customer_name,
        customer_phone: checkoutForm.customer_phone,
        note: checkoutForm.note || undefined,
        items: cart.map((i) => ({ inventory_id: i.id, quantity: i.qty })),
      });

      setCheckoutSuccess(`Pesanan berhasil dikirim. Kode pesanan: ${res.data.group_id}`);
      clearCart();
    } catch (err: any) {
      setCheckoutError(err.response?.data?.message || 'Gagal mengirim pesanan');
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  // ── PO cart ───────────────────────────────────────────
  const addToPOCart = (product: Product) => {
    setPOCart((prev) => {
      const exist = prev.find((i) => i.id === product.id);
      if (exist) return prev.map((i) => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...product, qty: 1 }];
    });
    setPOAddedId(product.id);
    setTimeout(() => setPOAddedId(null), 1200);
  };
  const updatePOQty = (id: number, qty: number) => {
    if (qty <= 0) setPOCart((prev) => prev.filter((i) => i.id !== id));
    else setPOCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };
  const clearPOCart = () => setPOCart([]);
  const poTotalItems = poCart.reduce((acc, i) => acc + i.qty, 0);
  const poTotalPrice = poCart.reduce((acc, i) => acc + i.qty * i.selling_price, 0);

  const openPOCheckout = () => {
    setPOCheckoutForm({ customer_name: '', customer_phone: '', note: '' });
    setPOError('');
    setPOSuccess('');
    setShowPOCheckout(true);
  };

  const handlePOCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setPOSubmitting(true);
    setPOError('');
    try {
      const res = await axios.post(`${BASE_URL}/po-orders/createBulk`, {
        customer_name: poCheckoutForm.customer_name,
        customer_phone: poCheckoutForm.customer_phone,
        note: poCheckoutForm.note || undefined,
        items: poCart.map((i) => ({ inventory_id: i.id, quantity: i.qty })),
      });

      // Kirim WA ke admin
      const lines = poCart.map((i) => `• ${i.name} x${i.qty} = ${rupiahFormat(i.qty * i.selling_price)}`).join('\n');
      const msg =
        `Halo, saya ingin melakukan *Pesanan PO* 🏷️\n\n` +
        `*Nama:* ${poCheckoutForm.customer_name}\n` +
        `*No. HP:* ${poCheckoutForm.customer_phone}\n` +
        (poCheckoutForm.note ? `*Catatan:* ${poCheckoutForm.note}\n` : '') +
        `\n*Produk yang dipesan:*\n${lines}\n\n` +
        `*Total Estimasi: ${rupiahFormat(poTotalPrice)}*\n` +
        `*Kode Pesanan: ${res.data.group_id}*\n\n` +
        `Mohon konfirmasi ketersediaan. Terima kasih! 🙏`;
      window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');

      setPOSuccess('Pesanan PO berhasil dikirim! Kami akan segera menghubungi Anda via WhatsApp.');
      clearPOCart();
    } catch (err: any) {
      setPOError(err.response?.data?.message || 'Gagal mengirim pesanan PO');
    } finally {
      setPOSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="TemanSkrispy" className="h-10 w-auto" />
            <span className="text-gray-400 text-xs hidden sm:inline">Katalog Produk</span>
          </div>

          {/* Search */}
          <div className="flex-1 max-w-md relative hidden sm:block">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Cari produk..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div className="flex items-center gap-2">
            {/* PO Cart button */}
            {poTotalItems > 0 && (
              <button onClick={() => setShowPOCart(true)}
                className="relative flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600 transition">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="hidden sm:inline">Pesanan PO</span>
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {poTotalItems}
                </span>
              </button>
            )}

            {/* Regular cart button */}
            <button onClick={() => setShowCart(true)}
              className="relative flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="hidden sm:inline">Keranjang</span>
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div className="sm:hidden px-4 pb-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Cari produk..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-6">{error}</div>}

        {/* Filter type */}
        {hasPO && (
          <div className="flex gap-2 mb-6 flex-wrap">
            {([['all', 'Semua'], ['ready', '✅ Ready Stock'], ['po', '🏷️ Pre-Order']] as const).map(([val, label]) => (
              <button key={val} onClick={() => setFilterType(val)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition ${filterType === val
                  ? val === 'po' ? 'bg-orange-500 text-white border-orange-500' : 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg className="animate-spin w-8 h-8 mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Memuat produk...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <svg className="w-16 h-16 mb-4 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="text-lg font-medium text-gray-500">Produk tidak ditemukan</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((product) => {
              const inCart = cart.find((i) => i.id === product.id);
              const inPOCart = poCart.find((i) => i.id === product.id);
              const isAdded = addedId === product.id;
              const isPOAdded = poAddedId === product.id;
              return (
                <div key={product.id}
                  className={`bg-white rounded-2xl border shadow-sm hover:shadow-md transition overflow-hidden flex flex-col ${!!product.is_po ? 'border-orange-200' : 'border-gray-100'}`}>
                  {!!product.is_po && (
                    <div className="bg-orange-500 text-white text-xs font-bold px-3 py-1 flex items-center gap-1">
                      <span>🏷️</span> PRE-ORDER
                    </div>
                  )}
                  <div className="h-44 bg-white overflow-hidden flex items-center justify-center p-2">
                    {product.image ? (
                      <img src={`${IMG_BASE}${product.image}`} alt={product.name}
                        className="w-full h-full object-contain"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <svg className={`w-14 h-14 ${!!product.is_po ? 'text-orange-200' : 'text-indigo-200'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>

                  <div className="p-3 flex flex-col flex-1">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 text-sm leading-tight line-clamp-2">{product.name}</h3>
                      {product.description && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{product.description}</p>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-gray-800 text-sm">{rupiahFormat(product.selling_price)}</span>
                        {!!product.is_po ? (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-600">PO</span>
                        ) : (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${product.stock <= 5 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            Stok {product.stock}
                          </span>
                        )}
                      </div>

                      {!!product.is_po ? (
                        inPOCart ? (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updatePOQty(product.id, inPOCart.qty - 1)}
                              className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-bold transition text-sm">−</button>
                            <span className="flex-1 text-center text-sm font-semibold text-gray-800">{inPOCart.qty}</span>
                            <button onClick={() => updatePOQty(product.id, inPOCart.qty + 1)}
                              className="w-7 h-7 rounded-lg bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white font-bold transition text-sm">+</button>
                          </div>
                        ) : (
                          <button onClick={() => addToPOCart(product)}
                            className={`w-full py-2 rounded-xl text-sm font-medium transition ${isPOAdded ? 'bg-emerald-500 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}>
                            {isPOAdded ? '✓ Ditambahkan' : '📋 Pesan PO'}
                          </button>
                        )
                      ) : inCart ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateQty(product.id, inCart.qty - 1)}
                            className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 font-bold transition text-sm">−</button>
                          <span className="flex-1 text-center text-sm font-semibold text-gray-800">{inCart.qty}</span>
                          <button onClick={() => updateQty(product.id, inCart.qty + 1)}
                            disabled={inCart.qty >= product.stock}
                            className="w-7 h-7 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center text-white font-bold transition text-sm">+</button>
                        </div>
                      ) : (
                        <button onClick={() => addToCart(product)}
                          className={`w-full py-2 rounded-xl text-sm font-medium transition ${isAdded ? 'bg-emerald-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}>
                          {isAdded ? '✓ Ditambahkan' : '+ Tambah'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* ── PO Cart Drawer ────────────────────────────────── */}
      {showPOCart && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40" onClick={() => setShowPOCart(false)} />
          <div className="w-full max-w-sm bg-white h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">🏷️ Keranjang PO</h2>
                <p className="text-xs text-gray-400 mt-0.5">{poTotalItems} item pre-order</p>
              </div>
              <button onClick={() => setShowPOCart(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {poCart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                  <svg className="w-14 h-14 mb-3 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="font-medium text-gray-500">Keranjang PO kosong</p>
                  <button onClick={() => setShowPOCart(false)}
                    className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition">
                    Lihat Produk
                  </button>
                </div>
              ) : poCart.map((item) => (
                <div key={item.id} className="flex items-center gap-3 bg-orange-50 rounded-xl p-3">
                  <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 border border-orange-100">
                    {item.image ? (
                      <img src={`${IMG_BASE}${item.image}`} alt={item.name} className="w-full h-full object-contain" />
                    ) : (
                      <svg className="w-6 h-6 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">{rupiahFormat(item.selling_price)} / pcs</p>
                    <p className="text-sm font-bold text-orange-600 mt-0.5">{rupiahFormat(item.qty * item.selling_price)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => updatePOQty(item.id, item.qty - 1)}
                      className="w-7 h-7 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 font-bold text-sm transition">−</button>
                    <span className="w-6 text-center text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => updatePOQty(item.id, item.qty + 1)}
                      className="w-7 h-7 rounded-lg bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white font-bold text-sm transition">+</button>
                  </div>
                </div>
              ))}
            </div>

            {poCart.length > 0 && (
              <div className="p-4 border-t border-gray-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">Estimasi Total</span>
                  <span className="text-xl font-bold text-gray-800">{rupiahFormat(poTotalPrice)}</span>
                </div>
                <button onClick={openPOCheckout}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition">
                  📋 Kirim Pesanan PO
                </button>
                <button onClick={clearPOCart} className="w-full text-sm text-gray-400 hover:text-red-500 transition py-1">
                  Kosongkan keranjang PO
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PO Checkout Modal ─────────────────────────────── */}
      {showPOCheckout && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Konfirmasi Pesanan PO</h2>
                <p className="text-xs text-gray-400 mt-0.5">{poCart.length} produk · {rupiahFormat(poTotalPrice)}</p>
              </div>
              <button onClick={() => setShowPOCheckout(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {poSuccess ? (
              <div className="px-6 py-8 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-800 mb-1">Pesanan PO Terkirim! 🎉</p>
                <p className="text-sm text-gray-500 mb-6">{poSuccess}</p>
                <button onClick={() => { setShowPOCheckout(false); setShowPOCart(false); }}
                  className="px-6 py-2.5 bg-orange-500 text-white rounded-xl text-sm font-medium hover:bg-orange-600 transition">
                  Tutup
                </button>
              </div>
            ) : (
              <form onSubmit={handlePOCheckout} className="px-6 py-5 space-y-4">
                {/* Summary */}
                <div className="bg-orange-50 rounded-xl p-3 space-y-1.5">
                  {poCart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-700 truncate mr-2">{item.name} <span className="text-gray-400">x{item.qty}</span></span>
                      <span className="text-orange-700 font-medium flex-shrink-0">{rupiahFormat(item.qty * item.selling_price)}</span>
                    </div>
                  ))}
                  <div className="pt-1.5 border-t border-orange-100 flex justify-between text-sm font-bold">
                    <span className="text-orange-800">Total Estimasi</span>
                    <span className="text-orange-700">{rupiahFormat(poTotalPrice)}</span>
                  </div>
                </div>

                {poError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {poError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama <span className="text-red-500">*</span></label>
                    <input type="text" required value={poCheckoutForm.customer_name}
                      onChange={(e) => setPOCheckoutForm({ ...poCheckoutForm, customer_name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="Nama lengkap" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">No. WhatsApp <span className="text-red-500">*</span></label>
                    <input type="tel" required value={poCheckoutForm.customer_phone}
                      onChange={(e) => setPOCheckoutForm({ ...poCheckoutForm, customer_phone: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      placeholder="08xxxxxxxxxx"
                      minLength={9}
                      maxLength={15} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catatan (opsional)</label>
                  <textarea rows={2} value={poCheckoutForm.note}
                    onChange={(e) => setPOCheckoutForm({ ...poCheckoutForm, note: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                    placeholder="Alamat pengiriman, varian, atau catatan tambahan..." />
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowPOCheckout(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                    Kembali
                  </button>
                  <button type="submit" disabled={poSubmitting}
                    className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-medium disabled:opacity-60 transition">
                    {poSubmitting ? 'Mengirim...' : '📋 Kirim Pesanan'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Regular Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Konfirmasi Pesanan</h2>
                <p className="text-xs text-gray-400 mt-0.5">{cart.length} produk · {rupiahFormat(totalPrice)}</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {checkoutSuccess ? (
              <div className="px-6 py-8 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-800 mb-1">Pesanan terkirim</p>
                <p className="text-sm text-gray-500 mb-6">{checkoutSuccess}</p>
                <button onClick={() => { setShowCheckout(false); setShowCart(false); }}
                  className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition">
                  Tutup
                </button>
              </div>
            ) : (
              <form onSubmit={handleCheckout} className="px-6 py-5 space-y-4">
                <div className="bg-emerald-50 rounded-xl p-3 space-y-1.5">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-700 truncate mr-2">{item.name} <span className="text-gray-400">x{item.qty}</span></span>
                      <span className="text-emerald-700 font-medium flex-shrink-0">{rupiahFormat(item.qty * item.selling_price)}</span>
                    </div>
                  ))}
                  <div className="pt-1.5 border-t border-emerald-100 flex justify-between text-sm font-bold">
                    <span className="text-emerald-800">Total</span>
                    <span className="text-emerald-700">{rupiahFormat(totalPrice)}</span>
                  </div>
                </div>

                {checkoutError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {checkoutError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nama <span className="text-red-500">*</span></label>
                    <input type="text" required autoFocus value={checkoutForm.customer_name}
                      onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      placeholder="Nama lengkap" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">No. WhatsApp <span className="text-red-500">*</span></label>
                    <input type="tel" required value={checkoutForm.customer_phone}
                      onChange={(e) => setCheckoutForm({ ...checkoutForm, customer_phone: e.target.value.replace(/\D/g, '') })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      placeholder="08xxxxxxxxxx"
                      minLength={9}
                      maxLength={15} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catatan (opsional)</label>
                  <textarea rows={2} value={checkoutForm.note}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, note: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                    placeholder="Alamat, jam kirim, atau catatan tambahan..." />
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowCheckout(false)}
                    className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                    Kembali
                  </button>
                  <button type="submit" disabled={checkoutSubmitting}
                    className="flex-1 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-medium disabled:opacity-60 transition">
                    {checkoutSubmitting ? 'Mengirim...' : 'Kirim Pesanan'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Regular Cart Drawer ───────────────────────────── */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/40" onClick={() => setShowCart(false)} />
          <div className="w-full max-w-sm bg-white h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Keranjang</h2>
                <p className="text-xs text-gray-400 mt-0.5">{totalItems} item</p>
              </div>
              <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-gray-600 p-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
                  <svg className="w-16 h-16 mb-4 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="font-medium text-gray-500">Keranjang kosong</p>
                  <button onClick={() => setShowCart(false)}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
                    Lihat Produk
                  </button>
                </div>
              ) : cart.map((item) => (
                <div key={item.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                    {item.image ? (
                      <img src={`${IMG_BASE}${item.image}`} alt={item.name} className="w-full h-full object-contain" />
                    ) : (
                      <svg className="w-6 h-6 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{item.name}</p>
                    <p className="text-xs text-gray-400">{rupiahFormat(item.selling_price)} / pcs</p>
                    <p className="text-sm font-bold text-indigo-600 mt-0.5">{rupiahFormat(item.qty * item.selling_price)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => updateQty(item.id, item.qty - 1)}
                      className="w-7 h-7 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center text-gray-600 font-bold text-sm transition">−</button>
                    <span className="w-6 text-center text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => updateQty(item.id, item.qty + 1)} disabled={item.qty >= item.stock}
                      className="w-7 h-7 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center text-white font-bold text-sm transition">+</button>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="p-4 border-t border-gray-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">Total</span>
                  <span className="text-xl font-bold text-gray-800">{rupiahFormat(totalPrice)}</span>
                </div>
                <button onClick={openCheckout}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm transition">
                  Kirim Pesanan
                </button>
                <button onClick={clearCart} className="w-full text-sm text-gray-400 hover:text-red-500 transition py-1">
                  Kosongkan keranjang
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
